import {
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  computed,
  signal,
} from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgxIntlTelInputModule,
  SearchCountryField,
  CountryISO,
} from 'ngx-intl-tel-input';
import { delay, of, Subscription } from 'rxjs';
import { MobileInputModel } from './mobile.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormBuilderService } from '../../services/form-builder/form-builder.service';

@Component({
  selector: 'lib-mobile-input',
  imports: [NgxIntlTelInputModule, ReactiveFormsModule, TranslocoPipe],
  templateUrl: './mobile-input.component.html',
  styleUrl: './mobile-input.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MobileInputComponent implements OnInit, AfterViewInit, OnDestroy {
  cd = inject(ChangeDetectorRef);
  formBuilderService = inject(FormBuilderService);
  private readonly transloco = inject(TranslocoService);
  // Expose enums for template
  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  selectedCountryISO = CountryISO.SaudiArabia;

  //inputs
  id = input.required<string>();
  name = input.required<string>();
  label = input.required<string>();
  inputFormControlName = input.required<string>();
  searchPlaceholder = input<string>('');
  placeholder = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  viewOnly = input<boolean>(false);
  equation = input<string>();
  viewValue = model<string>();
  selectedCountry = model<string>();
  selectedPhoneNumber = model<string>();

  displayPlaceholder = computed(() => {
    const providedPlaceholder = this.placeholder();
    if (providedPlaceholder) {
      return providedPlaceholder;
    }
    return this.transloco.translate('mobile_input_default_placeholder');
  });

  formGroup!: FormGroup;

  readonly subscription = new Subscription();
  private currentErrors: any = null;

  private readonly parentContainer = inject(ControlContainer);

  private get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  protected get childControl() {
    return this.parentFormGroup.controls[
      this.inputFormControlName()
    ] as FormControl;
  }

  isDoneEquationAction = signal(true);

  ngOnInit(): void {
    const validators = this.required() ? [Validators.required] : [];
    const isDisabled = this.disabled() || this.childControl.disabled;

    this.formGroup = new FormGroup({
      phone: new FormControl({ value: null, disabled: isDisabled }, validators),
    });

    if (this.selectedCountry() && this.selectedPhoneNumber()) {
      this.setPhoneValue();
    }
    this.handleEquation();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.childControl.statusChanges.subscribe(value => {
        if (value && this.selectedCountry() && this.selectedPhoneNumber()) {
          if (!this.formGroup.controls['phone'].value) {
            this.setPhoneValue();
            this.cd.detectChanges();
          }
        }
      })
    );

    // Subscribe to parent form validation events to re-apply errors when they get cleared
    this.subscription.add(
      this.parentFormGroup.statusChanges.subscribe(() => {
        // If we have stored errors and the child control has no errors (was cleared by parent),
        // re-apply our stored errors
        if (
          this.currentErrors &&
          !this.childControl.errors &&
          this.formGroup.controls['phone'].invalid
        ) {
          this.childControl.setErrors(this.currentErrors);
        }
      })
    );

    this.subscription.add(
      this.childControl.valueChanges.subscribe(() => {
        if (this.formGroup.controls['phone'].invalid) {
          this.currentErrors = { ...this.formGroup.controls['phone'].errors };
        } else {
          this.currentErrors = null;
        }
      })
    );
  }

  setPhoneValue() {
    this.formGroup.controls['phone'].setValue({
      number: this.selectedPhoneNumber(),
      countryCode: this.selectedCountry(),
    });
    this.selectedCountryISO = this.selectedCountry() as CountryISO;
  }

  onInputValueChange(event: MobileInputModel) {
    if (event) {
      this.childControl.setValue(event.e164Number);
      this.selectedCountryISO = event.countryCode as CountryISO;
      this.childControl.markAsDirty();
      this.childControl.markAsTouched();

      this.selectedCountry.set(event.countryCode);
      this.selectedPhoneNumber.set(event.number.replace(/\s/g, ''));

      const numberStart = event.number.split(' ')[0];
      if (
        (numberStart === '50' ||
          numberStart === '55' ||
          numberStart === '57') &&
        this.selectedCountryISO.toLocaleLowerCase() ===
          CountryISO.Egypt.toLocaleLowerCase()
      ) {
        this.childControl.setErrors({
          ...this.childControl.errors,
          validatePhoneNumber: true,
        });
      }
    } else {
      this.selectedCountry.set('');
      this.selectedPhoneNumber.set('');
      this.childControl.setValue('');
    }
  }
  handleEquation() {
    if (this.equation()) {
      const dynamicEquationParams = [
        this.equation() as string,
        this.inputFormControlName() as string,
        this.parentFormGroup,
      ];

      if (!this.equation()?.includes(' ')) {
        this.handleSingleEquation(dynamicEquationParams);
      }
    }
  }

  handleSingleEquation(dynamicEquationParams: (string | FormGroup)[]) {
    const equationControl = this.formBuilderService.handleLookupEquationKey(
      (dynamicEquationParams[0] as string).substring(1),
      dynamicEquationParams[1] as string,
      dynamicEquationParams[2] as FormGroup
    );

    // to handle lookup equation value
    if (equationControl) {
      const equationSub = equationControl.valueChanges.subscribe(value => {
        if (this.isDoneEquationAction()) {
          this.isDoneEquationAction.set(false);
          if (value) {
            // Set the internal form group value
            this.formGroup.controls['phone'].setValue({
              number: value.normalized_number || '',
              countryCode: value.country || '',
            });
            // Handle the model object structure
            this.selectedCountry.set(value.country || '');
            this.selectedPhoneNumber.set(value.normalized_number || '');

            // Update the form control with the international number
            this.childControl.setValue(value.international_number || '');
            this.selectedCountryISO = value.country as CountryISO;
          } else {
            this.selectedCountry.set('');
            this.selectedPhoneNumber.set('');
            this.childControl.setValue('');
            this.formGroup.controls['phone'].setValue({
              number: '',
              countryCode: '',
            });
            this.selectedCountryISO = CountryISO.SaudiArabia;
            this.childControl.markAsPristine();
            this.childControl.markAsUntouched();
          }
        }

        of(null)
          .pipe(delay(100))
          .subscribe(() => {
            this.isDoneEquationAction.set(true);
          });
      });

      this.subscription.add(equationSub);
    }
  }
}
